/**
 * @page EliminationPage
 * @description 玩家淘汰記錄（UI-2-6）
 * 選擇淘汰玩家 + 名次 + 淘汰者（Bounty 用）
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { PlayerEntryDto, EliminatePlayerDto } from '@pokerroom/types';
import { api } from '../../lib/api.ts';

async function fetchEntries(tournamentId: string): Promise<PlayerEntryDto[]> {
  return api.get<PlayerEntryDto[]>(`/tournament/${tournamentId}/entries`);
}

async function eliminatePlayer(tournamentId: string, dto: EliminatePlayerDto) {
  return api.post(`/tournament/${tournamentId}/eliminate`, dto);
}

export default function EliminationPage() {
  const { t } = useTranslation('tournament');
  const { id: tournamentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedEntryId, setSelectedEntryId] = useState('');
  const [eliminatedByEntryId, setEliminatedByEntryId] = useState('');
  const [error, setError] = useState('');

  const { data: entries = [] } = useQuery({
    queryKey: ['tournament-entries', tournamentId],
    queryFn: () => fetchEntries(tournamentId!),
    enabled: !!tournamentId,
  });

  const playing = entries.filter((e) => e.status === 'PLAYING' || e.status === 'FINAL_TABLE');
  const eliminated = entries.filter((e) => e.status === 'ELIMINATED');
  const nextRank = entries.length - eliminated.length;

  const eliminateMutation = useMutation({
    mutationFn: (dto: EliminatePlayerDto) => eliminatePlayer(tournamentId!, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tournament-entries', tournamentId] });
      setSelectedEntryId('');
      setEliminatedByEntryId('');
      setError('');
    },
    onError: (err: Error) => setError(err.message),
  });

  const handleEliminate = () => {
    if (!selectedEntryId) return;
    eliminateMutation.mutate({
      entryId: selectedEntryId,
      rank: nextRank,
      eliminatedByEntryId: eliminatedByEntryId || undefined,
    });
  };

  return (
    <div className="p-6 max-w-xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="text-base-content/70 hover:text-base-content/70 text-sm mb-4 block"
      >
        {t('elimination.back')}
      </button>
      <h1 className="text-2xl font-bold text-base-content mb-6">{t('elimination.title')}</h1>

      {/* 統計 */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-success">{playing.length}</div>
          <div className="text-xs text-success/70 mt-1">{t('elimination.activePlayers')}</div>
        </div>
        <div className="flex-1 bg-base-300 border border-base-300 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-base-content">#{nextRank}</div>
          <div className="text-xs text-base-content/70 mt-1">{t('elimination.nextRank')}</div>
        </div>
      </div>

      {/* 選擇被淘汰玩家 */}
      <div className="bg-base-300 border border-base-300 rounded-xl p-4 mb-4">
        <label className="text-base-content/70 text-sm block mb-2">
          {t('elimination.eliminatedPlayer')}
        </label>
        <div className="grid gap-2">
          {playing.map((entry) => (
            <button
              key={entry.id}
              onClick={() => setSelectedEntryId(entry.id)}
              className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-colors ${
                selectedEntryId === entry.id
                  ? 'border-red-500 bg-red-500/20'
                  : 'border-white/10 hover:border-white/30 bg-white/5'
              }`}
            >
              <span className="text-base-content font-medium">{entry.playerName}</span>
              {entry.tableNo && (
                <span className="text-base-content/70 text-sm">
                  {t('elimination.tableAndSeat', { table: entry.tableNo, seat: entry.seatNo })}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* 選擇淘汰者（Bounty） */}
      <div className="bg-base-300 border border-base-300 rounded-xl p-4 mb-6">
        <label className="text-base-content/70 text-sm block mb-2">
          {t('elimination.eliminatedBy')}
        </label>
        <select
          value={eliminatedByEntryId}
          onChange={(e) => setEliminatedByEntryId(e.target.value)}
          className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2"
        >
          <option value="">{t('elimination.noSpecify')}</option>
          {playing
            .filter((e) => e.id !== selectedEntryId)
            .map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.playerName}
              </option>
            ))}
        </select>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3 text-error text-sm mb-4">
          {error}
        </div>
      )}

      <button
        disabled={!selectedEntryId || eliminateMutation.isPending}
        onClick={handleEliminate}
        className="w-full py-4 bg-red-600 text-white rounded-xl font-bold text-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
      >
        {eliminateMutation.isPending
          ? t('elimination.recording')
          : t('elimination.confirmEliminate', { rank: nextRank })}
      </button>

      {/* 已淘汰記錄 */}
      {eliminated.length > 0 && (
        <div className="mt-8">
          <h2 className="text-base-content/70 text-sm mb-3">{t('elimination.eliminatedList')}</h2>
          <div className="space-y-2">
            {[...eliminated]
              .sort((a, b) => (b.finalRank ?? 0) - (a.finalRank ?? 0))
              .map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between px-4 py-3 bg-base-300 rounded-xl"
                >
                  <span className="text-base-content/70">{entry.playerName}</span>
                  <span className="text-base-content/70 text-sm">#{entry.finalRank}</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
